/**
 * Settings Page
 * 
 * Central hub for user/provider settings
 * Routes to appropriate settings based on user type
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth } from '@/lib/context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Bell, 
  User, 
  MapPin, 
  Shield, 
  Globe, 
  CreditCard,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';

const SETTINGS_LABELS = {
  en: {
    title: 'Settings',
    subtitle: 'Manage your account and preferences',
    notifications: 'Notifications',
    notificationsDesc: 'Email and alert preferences',
    providerPrefs: 'Service Preferences',
    providerPrefsDesc: 'Categories and service areas',
    mapVisibility: 'Map Visibility',
    mapVisibilityDesc: 'Control your visibility on the public map',
    account: 'Account',
    accountDesc: 'Profile and security settings',
    language: 'Language',
    languageDesc: 'Change display language',
    billing: 'Billing',
    billingDesc: 'Subscription and payment methods',
    backToDashboard: 'Back to Dashboard',
    providerDashboard: 'Provider Dashboard'
  },
  et: {
    title: 'Seaded',
    subtitle: 'Halda oma kontot ja eelistusi',
    notifications: 'Teavitused',
    notificationsDesc: 'E-posti ja teavituste eelistused',
    providerPrefs: 'Teenuse eelistused',
    providerPrefsDesc: 'Kategooriad ja teeninduspiirkonnad',
    mapVisibility: 'Kaardi nähtavus',
    mapVisibilityDesc: 'Kontrolli oma nähtavust avalikul kaardil',
    account: 'Konto',
    accountDesc: 'Profiili ja turvaseaded',
    language: 'Keel',
    languageDesc: 'Muuda kuvatavat keelt',
    billing: 'Arveldus',
    billingDesc: 'Tellimus ja makseviisid',
    backToDashboard: 'Tagasi töölaudale',
    providerDashboard: 'Teenusepakkuja töölaud'
  },
  fi: {
    title: 'Asetukset',
    subtitle: 'Hallitse tiliäsi ja asetuksiasi',
    notifications: 'Ilmoitukset',
    notificationsDesc: 'Sähköposti- ja hälytyskäytännöt',
    providerPrefs: 'Palveluasetukset',
    providerPrefsDesc: 'Kategoriat ja palvelualueet',
    mapVisibility: 'Kartan näkyvyys',
    mapVisibilityDesc: 'Hallitse näkyvyyttäsi julkisella kartalla',
    account: 'Tili',
    accountDesc: 'Profiili- ja turva-asetukset',
    language: 'Kieli',
    languageDesc: 'Vaihda näyttökieltä',
    billing: 'Laskutus',
    billingDesc: 'Tilaus ja maksutavat',
    backToDashboard: 'Takaisin hallintapaneeliin',
    providerDashboard: 'Palveluntarjoajan hallintapaneeli'
  },
  sv: {
    title: 'Inställningar',
    subtitle: 'Hantera ditt konto och preferenser',
    notifications: 'Notifikationer',
    notificationsDesc: 'E-post och aviseringspreferenser',
    providerPrefs: 'Tjänstpreferenser',
    providerPrefsDesc: 'Kategorier och serviceområden',
    mapVisibility: 'Kartsynlighet',
    mapVisibilityDesc: 'Kontrollera din synlighet på den offentliga kartan',
    account: 'Konto',
    accountDesc: 'Profil- och säkerhetsinställningar',
    language: 'Språk',
    languageDesc: 'Ändra visningsspråk',
    billing: 'Fakturering',
    billingDesc: 'Prenumeration och betalningsmetoder',
    backToDashboard: 'Tillbaka till instrumentpanelen',
    providerDashboard: 'Leverantörspanel'
  },
  nb: {
    title: 'Innstillinger',
    subtitle: 'Administrer kontoen din og preferanser',
    notifications: 'Varsler',
    notificationsDesc: 'E-post og varselpreferanser',
    providerPrefs: 'Tjenestepreferanser',
    providerPrefsDesc: 'Kategorier og tjenesteområder',
    mapVisibility: 'Kartsynlighet',
    mapVisibilityDesc: 'Kontroller synligheten din på det offentlige kartet',
    account: 'Konto',
    accountDesc: 'Profil- og sikkerhetsinnstillinger',
    language: 'Språk',
    languageDesc: 'Endre visningsspråk',
    billing: 'Fakturering',
    billingDesc: 'Abonnement og betalingsmetoder',
    backToDashboard: 'Tilbake til dashbordet',
    providerDashboard: 'Leverandørpanel'
  },
  ru: {
    title: 'Настройки',
    subtitle: 'Управление аккаунтом и предпочтениями',
    notifications: 'Уведомления',
    notificationsDesc: 'Настройки email и оповещений',
    providerPrefs: 'Настройки услуг',
    providerPrefsDesc: 'Категории и зоны обслуживания',
    mapVisibility: 'Видимость на карте',
    mapVisibilityDesc: 'Управление видимостью на публичной карте',
    account: 'Аккаунт',
    accountDesc: 'Профиль и безопасность',
    language: 'Язык',
    languageDesc: 'Изменить язык интерфейса',
    billing: 'Оплата',
    billingDesc: 'Подписка и способы оплаты',
    backToDashboard: 'Назад к панели',
    providerDashboard: 'Панель поставщика'
  },
  es: {
    title: 'Configuración',
    subtitle: 'Gestiona tu cuenta y preferencias',
    notifications: 'Notificaciones',
    notificationsDesc: 'Preferencias de correo y alertas',
    providerPrefs: 'Preferencias de servicio',
    providerPrefsDesc: 'Categorías y áreas de servicio',
    mapVisibility: 'Visibilidad en el mapa',
    mapVisibilityDesc: 'Controla tu visibilidad en el mapa público',
    account: 'Cuenta',
    accountDesc: 'Configuración de perfil y seguridad',
    language: 'Idioma',
    languageDesc: 'Cambiar idioma de visualización',
    billing: 'Facturación',
    billingDesc: 'Suscripción y métodos de pago',
    backToDashboard: 'Volver al panel',
    providerDashboard: 'Panel de proveedor'
  }
};

const SettingsPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const l = SETTINGS_LABELS[language] || SETTINGS_LABELS.en;
  
  // Check if user is a provider (has provider_session)
  const isProvider = !!localStorage.getItem('provider_session');

  const settingsItems = [
    {
      id: 'notifications',
      icon: Bell,
      title: l.notifications,
      description: l.notificationsDesc,
      link: '/settings/alerts',
      show: true
    },
    {
      id: 'provider-prefs',
      icon: User,
      title: l.providerPrefs,
      description: l.providerPrefsDesc,
      link: '/settings/provider-preferences',
      show: true
    },
    {
      id: 'map-visibility',
      icon: MapPin,
      title: l.mapVisibility,
      description: l.mapVisibilityDesc,
      link: '/provider/map-visibility',
      show: isProvider
    },
    {
      id: 'language',
      icon: Globe,
      title: l.language,
      description: l.languageDesc,
      link: null, // Handled by language selector in nav
      show: true
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-orange-600">KEIKKA</Link>
          <div className="flex items-center gap-3">
            {isProvider ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/provider/dashboard">
                  {l.providerDashboard}
                </Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {l.backToDashboard}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{l.title}</h1>
          <p className="text-gray-600 mt-1">{l.subtitle}</p>
        </div>

        <div className="grid gap-4">
          {settingsItems.filter(item => item.show).map((item) => (
            <Card 
              key={item.id} 
              className={`transition-colors ${item.link ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
              onClick={() => item.link && navigate(item.link)}
              data-testid={`settings-${item.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <item.icon className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{item.title}</h3>
                      <p className="text-sm text-gray-500">{item.description}</p>
                    </div>
                  </div>
                  {item.link && (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Provider Dashboard Quick Link for Providers */}
        {isProvider && (
          <Card className="mt-6 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-orange-800">{l.providerDashboard}</h3>
                  <p className="text-sm text-orange-600">{l.mapVisibilityDesc}</p>
                </div>
                <Button asChild className="bg-orange-600 hover:bg-orange-700">
                  <Link to="/provider/dashboard">
                    <MapPin className="h-4 w-4 mr-2" />
                    Open
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SettingsPage;
